import { PureComponent } from 'react';

import CoreUtils from '../utils/CoreUtils';
import DomUtils from '../utils/DomUtils';
import StringUtils from '../utils/StringUtils';

import PropertyPageView from './PropertyPageView';
import PropertyPageDataModel from './PropertyPageDataModel';

export default class PropertySheetView extends PureComponent {
  static displayName = PropertySheetView.name;

  constructor(props) {
    super(props);

    this.submitChangesHandler = this.onSubmitChanges.bind(this);
    this.revertChangesHandler = this.onRevertChanges.bind(this);
    this.modificationStateHandler = this.onModificationStateUpdated.bind(this);

    this.rootElementId = `PwxPropertySheet_${crypto.randomUUID()}`;
    this.buttonBarElementId = `PwxPropertySheetButtonBar_${crypto.randomUUID()}`;
    this.contentElementId = `PwxPropertySheetContent_${crypto.randomUUID()}`;

    this.parentElement = null;
    this.resizeObserver = null;
    this.rootElement = null;
    this.buttonBarElement = null;
    this.contentElement = null;

    var pages = this.generatePages();

    var activePageIndex = null;
    if (pages.length > 0)
      activePageIndex = 0;

    if (activePageIndex !== null)
      this.activatePage(pages[activePageIndex]);

    this.state = {
      pages: pages,
      activePageIndex: activePageIndex,
      isModified: this.resolveIsModified(pages),
      hasErorrs: this.resolveHasErrors(pages)
    };
  }

  componentDidMount() {
    this.dataModel = this.props.dataModel;
    this.dataModel.addEventListener('submitChanges', this.submitChangesHandler);
    this.dataModel.addEventListener('revertChanges', this.revertChangesHandler);

    this.rootElement = document.getElementById(this.rootElementId);
    this.buttonBarElement = document.getElementById(this.buttonBarElementId);
    this.contentElement = document.getElementById(this.contentElementId);

    if (!this.props.fillParent)
      return;

    this.parentElement = this.rootElement.parentElement;
    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.updateViewHeight()));
    this.resizeObserver.observe(this.parentElement);

    this.updateViewHeight();
  }

  componentWillUnmount() {
    this.releasePages(this.state.pages);

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.parentElement = null;

    this.rootElement = null;
    this.buttonBarElement = null;
    this.contentElement = null;

    this.dataModel.removeEventListener('revertChanges', this.revertChangesHandler);
    this.dataModel.removeEventListener('submitChanges', this.submitChangesHandler);
    this.dataModel = null;
  }

  render() {
    return (
      <div id={this.rootElementId} className="pwx-property-sheet-view" >
        <div id={this.buttonBarElementId} className="pwx-property-sheet-view-button-bar">
          {this.state.pages.map((page, index) => this.renderPageButton(page, index, this.state.activePageIndex)) }
        </div>
        <div id={this.contentElementId} className="pwx-property-sheet-view-content">
          { this.renderPageView(this.state.pages, this.state.activePageIndex) }
        </div>
      </div>
    );
  }

  renderPageButton(page, pageIndex, activePageIndex) {
    var classNames = 'pwx-page-button';
    if (pageIndex === activePageIndex)
      classNames += ' pwx-page-button-active';
    else
      classNames += ' pwx-page-button-inactive';

    return (
      <span key={page.guid} className={classNames} onClick={this.onPageSelected.bind(this, pageIndex)}>
        {page.label}
        &nbsp;
        <span className="pwx-modified-flag dx-icon-rename" hidden={!page.isModified} />
        <span className="pwx-error-flag dx-icon-warning" hidden={!page.hasErrors} />
      </span>
    );
  }

  renderPageView(pages, activePageIndex) {
    var page = pages[activePageIndex];
    if (CoreUtils.isUndefinedOrNull(page) || CoreUtils.isUndefinedOrNull(page.viewType))
      return (<div />);

    return (<page.viewType dataModel={page.dataModel} />)
  }

  updateViewHeight() {
    if (this.parentElement === null)
      return;

    var viewHeight = DomUtils.calculateInnerContentHeight(this.parentElement);
    var buttonBarHeight = DomUtils.calculateOuterContentHeight(this.buttonBarElement);
    var pagebedViewContentHeight = viewHeight - buttonBarHeight;

    DomUtils.setElementHeight(this.rootElement, viewHeight);
    DomUtils.setElementHeight(this.contentElement, pagebedViewContentHeight);
  }

  onPageSelected(pageIndex) {
    this.activatePage(this.state.pages[pageIndex]);

    this.setState({
      activePageIndex: pageIndex
    });
  }

  onModificationStateUpdated(dataModel, state) {
    var page = this.dataModelIndex[dataModel.guid];
    if (CoreUtils.isUndefinedOrNull(page))
      return;

    var updateCount = 0;

    if ((state.isModified !== undefined) && (state.isModified !== page.isModified)) {
      page.isModified = state.isModified;
      updateCount += 1;
    }

    if ((state.hasErrors !== undefined) && (state.hasErrors !== page.hasErrors)) {
      page.hasErrors = state.hasErrors;
      updateCount += 1;
    }

    if (updateCount === 0)
      return;

    this.triggerUpdatePageDisplay();

    if (CoreUtils.isDefinedAndNotNull(this.dataModel))
      this.dataModel.updateProperties({
        isModified: this.resolveIsModified(this.state.pages),
        hasErrors: this.resolveHasErrors(this.state.pages)
      });
  }

  triggerUpdatePageDisplay() {
    this.setState({
      pages: Array.from(this.state.pages)
    });
  }

  activatePage(page) {
    if (CoreUtils.isUndefinedOrNull(page) || CoreUtils.isUndefinedOrNull(page.dataModelType)
      || CoreUtils.isDefinedAndNotNull(page.dataModel))
      return;

    page.dataModel = new page.dataModelType(this.props.session, this.modificationStateHandler);
    this.dataModelIndex[page.dataModel.guid] = page;
  }

  onSubmitChanges(operationCompleteHandler) {
    if (!this.dataModel.isModified || this.dataModel.hasErrors)
      return;

    var submitMethods = [];

    var submitOperation = {
      operationCompleteHandler: operationCompleteHandler,
      results: {
        successes: 0,
        failures: 0
      },
      submitTaskCount: 0,
      completedTaskCount: 0
    };

    this.state.pages.forEach(page => {
      if (CoreUtils.isUndefinedOrNull(page.dataModel) || !page.isModified || page.hasErrors)
        return;

      submitMethods.push(page.dataModel.submitChanges.bind(page.dataModel));
      submitOperation.submitTaskCount += 1;
    });

    if (submitOperation.submitTaskCount === 0) {
      this.checkSubmitOperationResults(submitOperation);
      return;
    }

    submitMethods.forEach(
      submitMethod => submitMethod(this.onDataModelSubmitResult.bind(this, submitOperation))
    );
  }

  onDataModelSubmitResult(submitOperation, result) {
    submitOperation.results.successes += result.successes;
    submitOperation.results.failures += result.failures;
    submitOperation.completedTaskCount += 1;

    this.checkSubmitOperationResults(submitOperation);
  }

  checkSubmitOperationResults(submitOperation) {
    if (submitOperation.completedTaskCount < submitOperation.submitTaskCount)
      return;

    if (CoreUtils.isDefinedAndNotNull(submitOperation.operationCompleteHandler))
      submitOperation.operationCompleteHandler(submitOperation.results);
  }

  onRevertChanges() {
    if (!this.dataModel.isModified)
      return;

    var revertMethods = [];
    this.state.pages.forEach(page => {
      if (CoreUtils.isUndefinedOrNull(page.dataModel) || !page.isModified)
        return;

      revertMethods.push(page.dataModel.revertChanges.bind(page.dataModel));
    });

    revertMethods.forEach(revertMethod => revertMethod());
  }

  generatePages() {
    this.dataModelIndex = {};

    if (CoreUtils.isUndefinedOrNull(this.props.children))
      return [];

    var pages = [];
    if (Array.isArray(this.props.children))
      this.props.children.forEach(templatePage => this.addPage(templatePage, pages));
    else
      this.addPage(this.props.children, pages);

    return pages;
  }

  addPage(templatePage, pages) {
    // eslint-disable-next-line eqeqeq
    if (CoreUtils.isUndefinedOrNull(templatePage) || templatePage.type != PropertyPage)
      return;

    var pageLabel = '';
    if (StringUtils.isValidString(templatePage.props.title))
      pageLabel = templatePage.props.title;

    var viewType = null;
    if (CoreUtils.isDefinedAndNotNull(templatePage.props.viewType)
      // eslint-disable-next-line eqeqeq
      && Object.getPrototypeOf(templatePage.props.viewType) == PropertyPageView)
      viewType = templatePage.props.viewType;

    var dataModelType = null;
    if (CoreUtils.isDefinedAndNotNull(templatePage.props.dataModelType)
      // eslint-disable-next-line eqeqeq
      && Object.getPrototypeOf(templatePage.props.dataModelType) == PropertyPageDataModel)
      dataModelType = templatePage.props.dataModelType;

    pages.push(this.generatePage(pageLabel, viewType, dataModelType));
  }

  generatePage(label, viewType, dataModelType) {
    return {
      guid: crypto.randomUUID(),
      label: label,
      viewType: viewType,
      dataModelType: dataModelType,
      dataModel: null,
      isModified: false,
      hasErrors: false
    };
  }

  releasePages(pages) {
    pages.forEach(page => {
      if (CoreUtils.isDefinedAndNotNull(page.dataModel)) {
        page.dataModel.dispose();
        page.dataModel = null;
      }
    });

    this.dataModelIndex = {};
  }

  resolveIsModified(pages) {
    return pages.some(page => page.isModified);
  }

  resolveHasErrors(pages) {
    return pages.some(page => page.hasErrors);
  }
}

export class PropertyPage extends PureComponent { }